import { createFileRoute, Link } from "@tanstack/react-router";
import { Component, lazy, Suspense, useEffect, useRef, useState, type ErrorInfo, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { ShieldCheck } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { PageHeader } from "@/components/site/PageHeader";
import { adminDocsStatus } from "@/lib/admin-docs";
import { AdminDebugPanel, type RoleAttempt } from "@/components/admin/AdminDebugPanel";

const ProductsTab = lazy(() => import("@/components/admin/ProductsTab").then((m) => ({ default: m.ProductsTab })));
const UsersTab = lazy(() => import("@/components/admin/UsersTab").then((m) => ({ default: m.UsersTab })));
const AppearanceTab = lazy(() => import("@/components/admin/AppearanceTab").then((m) => ({ default: m.AppearanceTab })));
const MediaTab = lazy(() => import("@/components/admin/MediaTab").then((m) => ({ default: m.MediaTab })));
const MediaPickerField = lazy(() => import("@/components/admin/MediaTab").then((m) => ({ default: m.MediaPickerField })));
const AmazonTestsTab = lazy(() => import("@/components/admin/AmazonTestsTab").then((m) => ({ default: m.AmazonTestsTab })));
const MarketingTab = lazy(() => import("@/components/admin/MarketingTab").then((m) => ({ default: m.MarketingTab })));
const PagesWorkspaceTab = lazy(() => import("@/components/admin/PagesWorkspaceTab").then((m) => ({ default: m.PagesWorkspaceTab })));
const NavigationTab = lazy(() => import("@/components/admin/NavigationTab").then((m) => ({ default: m.NavigationTab })));
const HelpTab = lazy(() => import("@/components/admin/HelpTab").then((m) => ({ default: m.HelpTab })));
const ExperimentsTab = lazy(() => import("@/components/admin/ExperimentsTab").then((m) => ({ default: m.ExperimentsTab })));
const AnalyticsQaTab = lazy(() => import("@/components/admin/AnalyticsQaTab").then((m) => ({ default: m.AnalyticsQaTab })));
const PackagingTab = lazy(() => import("@/components/admin/PackagingTab").then((m) => ({ default: m.PackagingTab })));

import { ADMIN_TABS, ADMIN_TAB_DEFS, type AdminTab } from "@/lib/admin-tabs";


export const Route = createFileRoute("/admin")({
  ssr: false,
  head: () => ({ meta: [{ title: "Admin — Glow Long Golf" }] }),
  validateSearch: (
    search: Record<string, unknown>,
  ): { tab?: AdminTab; page?: string; mode?: "builder" } => {
    const rawTab = typeof search.tab === "string" ? (search.tab as AdminTab) : undefined;
    const page = typeof search.page === "string" ? search.page : undefined;
    // Legacy deep links: ?tab=builder now maps to the Pages tab in builder mode.
    if (rawTab === ("builder" as string)) return { tab: "pages", page, mode: "builder" };
    const tab = rawTab && ADMIN_TABS.includes(rawTab) ? rawTab : undefined;
    const mode = search.mode === "builder" ? ("builder" as const) : undefined;
    return { tab, page, mode };
  },
  component: AdminPage,
});

function AdminPage() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const tab: AdminTab = search.tab ?? "overview";
  const setTab = (next: AdminTab) =>
    navigate({ search: (prev: { tab?: AdminTab; page?: string }) => ({ ...prev, tab: next === "overview" ? undefined : next }), replace: true });
  const [lastRoleAttempt, setLastRoleAttempt] = useState<RoleAttempt>({
    startedAt: null,
    endedAt: null,
    durationMs: null,
    outcome: "idle",
    errorMessage: null,
    isAdmin: null,
    attempts: 0,
  });
  const attemptsRef = useRef(0);

  const {
    data: roleCheck,
    refetch: recheck,
    isLoading: roleLoading,
    isError: roleErrored,
    error: roleError,
  } = useQuery({
    queryKey: ["is-admin"],
    queryFn: async () => {
      const startedEpoch = Date.now();
      const startedPerf = performance.now();
      const nextAttempts = attemptsRef.current + 1;
      attemptsRef.current = nextAttempts;
      setLastRoleAttempt((prev) => ({
        ...prev,
        startedAt: startedEpoch,
        endedAt: null,
        durationMs: null,
        outcome: "pending",
        errorMessage: null,
        attempts: nextAttempts,
      }));
      console.info("[Admin] role:start", { attempt: nextAttempts });
      try {
        const sessionResult = await Promise.race([
          supabase.auth.getSession(),
          new Promise<null>((resolve) => window.setTimeout(() => resolve(null), 2500)),
        ]);
        const session = sessionResult?.data.session;
        if (!session?.user) throw new Error("Please sign in before opening admin.");

        const { data, error } = await supabase.rpc("has_role", {
          _user_id: session.user.id,
          _role: "admin",
        });
        if (error) throw new Error(error.message);

        const res = { isAdmin: data === true, userId: session.user.id };
        const durationMs = Math.round(performance.now() - startedPerf);
        const isAdminResult = res.isAdmin === true;
        console.info("[Admin] role:end", { durationMs, isAdmin: isAdminResult });
        setLastRoleAttempt((prev) => ({
          ...prev,
          endedAt: Date.now(),
          durationMs,
          outcome: "success",
          errorMessage: null,
          isAdmin: isAdminResult,
        }));
        return res;
      } catch (err) {
        const durationMs = Math.round(performance.now() - startedPerf);
        const message = err instanceof Error ? err.message : String(err);
        console.warn("[Admin] role:error", { durationMs, message, error: err });
        setLastRoleAttempt((prev) => ({
          ...prev,
          endedAt: Date.now(),
          durationMs,
          outcome: "error",
          errorMessage: message,
          isAdmin: null,
        }));
        throw err;
      }
    },
    retry: 1,
  });
  const isAdmin = (roleCheck as any)?.isAdmin === true;

  async function claim() {
    const sessionResult = await Promise.race([
      supabase.auth.getSession(),
      new Promise<null>((resolve) => window.setTimeout(() => resolve(null), 2500)),
    ]);
    if (!sessionResult?.data.session?.user) {
      toast.error("Please sign in first.");
      return;
    }

    const { data: claimed, error } = await supabase.rpc("claim_admin");
    if (error) {
      toast.error(error.message);
      return;
    }

    const { data: alreadyAdmin, error: roleError } = await supabase.rpc("has_role", {
      _user_id: sessionResult.data.session.user.id,
      _role: "admin",
    });
    if (roleError) {
      toast.error(roleError.message);
      return;
    }

    const res = { claimed, alreadyAdmin: alreadyAdmin === true };
    if (res.claimed || res.alreadyAdmin) {
      toast.success(res.claimed ? "You are now the admin." : "You're already an admin.");
      recheck();
    } else {
      toast.error("Admin already claimed by another account.");
    }
  }

  const debugPanel = (
    <AdminDebugPanel state={lastRoleAttempt} onRetry={() => void recheck()} />
  );

  if (roleLoading) {
    return (
      <section className="mx-auto max-w-6xl px-4 pt-6">
        {debugPanel}
        <div className="py-16 text-center">
          <ShieldCheck className="mx-auto h-12 w-12 animate-pulse text-primary" />
          <h1 className="mt-4 font-display text-2xl font-semibold">Checking admin access…</h1>
          <p className="mt-3 text-sm text-muted-foreground">Verifying your role with the server.</p>
        </div>
      </section>
    );
  }

  if (roleErrored) {
    const message = roleError instanceof Error ? roleError.message : "Unknown error";
    const looksLikeNetwork = /failed to fetch|network|load failed/i.test(message);
    return (
      <section className="mx-auto max-w-6xl px-4 pt-6">
        {debugPanel}
        <div className="py-16 text-center">
          <ShieldCheck className="mx-auto h-12 w-12 text-destructive" />
          <h1 className="mt-4 font-display text-2xl font-semibold">Couldn't verify admin access</h1>
          <p className="mt-3 text-sm text-muted-foreground">
            {looksLikeNetwork
              ? "The server may have restarted or the connection dropped. Try again in a moment."
              : message}
          </p>
          <div className="mt-6 flex justify-center gap-3">
            <button
              onClick={() => void recheck()}
              className="rounded-full bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground shadow-glow hover:opacity-90"
            >
              Retry
            </button>
            <button
              onClick={() => window.location.reload()}
              className="rounded-full border border-border px-6 py-3 text-sm font-semibold hover:bg-secondary"
            >
              Reload page
            </button>
          </div>
          <p className="mt-4 text-xs text-muted-foreground">Details: {message}</p>
        </div>
      </section>
    );
  }

  if (!isAdmin) {
    return (
      <section className="mx-auto max-w-6xl px-4 pt-6">
        {debugPanel}
        <div className="py-16 text-center">
          <ShieldCheck className="mx-auto h-12 w-12 text-primary" />
          <h1 className="mt-4 font-display text-2xl font-semibold">Admin access</h1>
          <p className="mt-3 text-muted-foreground">If you're the project owner and this is your first time, you can claim the admin role for this account.</p>
          <button onClick={claim} className="mt-6 inline-flex rounded-full bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground shadow-glow hover:opacity-90">
            Claim admin role
          </button>
        </div>
      </section>
    );
  }

  const tabs: readonly { id: AdminTab; label: string }[] = ADMIN_TAB_DEFS;


  const docs = adminDocsStatus();

  return (
    <>
      <PageHeader eyebrow="Admin" title="Glow Long Golf admin" lead="Manage site settings, appearance, media, products, tracking, Amazon, marketing connectors, orders, and leads." />
      <section className="mx-auto max-w-6xl px-4 pb-20 sm:px-6 lg:px-8">
        {debugPanel}
        {docs.isStale && (
          <div className="mb-4 flex items-start justify-between gap-3 rounded-xl border border-destructive/40 bg-destructive/5 p-3 text-sm">
            <div>
              <strong className="text-destructive">Admin docs are out of date.</strong>{" "}
              <span className="text-muted-foreground">
                Last updated {docs.lastUpdated} ({docs.ageDays} days ago, threshold {docs.staleAfterDays}).
                Review changes and bump <code>lastUpdated</code> in <code>src/lib/admin-docs.ts</code>.
              </span>
            </div>
            <button onClick={() => setTab("help")} className="shrink-0 rounded-full border border-destructive/40 px-3 py-1 text-xs font-semibold text-destructive hover:bg-destructive/10">
              Open Help
            </button>
          </div>
        )}
        <nav className="mb-6 flex flex-wrap gap-2 border-b border-border pb-3">
          {tabs.map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)} className={`relative rounded-full px-4 py-2 text-sm font-medium transition ${tab === t.id ? "bg-primary text-primary-foreground shadow-glow" : "border border-border bg-card hover:bg-secondary"}`}>
              {t.label}
              {t.id === "help" && (
                <span
                  className={`ml-2 inline-flex items-center rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${
                    docs.isStale
                      ? "bg-destructive text-destructive-foreground"
                      : tab === t.id
                        ? "bg-primary-foreground/20 text-primary-foreground"
                        : "bg-secondary text-muted-foreground"
                  }`}
                  title={`Docs last updated ${docs.lastUpdated} (${docs.ageDays}d ago)`}
                >
                  {docs.isStale ? `${docs.ageDays}d` : docs.lastUpdated.slice(5)}
                </span>
              )}
            </button>
          ))}
        </nav>
        <AdminTabBoundary tab={tab}>
          <Suspense fallback={<AdminTabLoading />}>
            {tab === "overview" && <OverviewTab />}
            {tab === "pages" && <PagesWorkspaceTab />}
            {tab === "navigation" && <NavigationTab />}
            {tab === "appearance" && <AppearanceTab />}
            {tab === "media" && <MediaTab />}
            {tab === "settings" && <SettingsTab />}
            {tab === "products" && <ProductsTab />}
            {tab === "packaging" && <PackagingTab />}
            {tab === "tracking" && <TrackingTab />}
            {tab === "amazon" && <AmazonTab />}
            {tab === "ab_tests" && <AmazonTestsTab />}
            {tab === "experiments" && <ExperimentsTab />}
            {tab === "marketing" && <MarketingTab />}
            {tab === "orders" && <OrdersTab />}
            {tab === "leads" && <LeadsTab />}
            {tab === "users" && <UsersTab currentUserId={(roleCheck as any)?.userId ?? ""} />}
            {tab === "analytics_qa" && <AnalyticsQaTab />}
            {tab === "help" && <HelpTab />}
          </Suspense>
        </AdminTabBoundary>
      </section>
    </>
  );
}

function AdminTabLoading() {
  return (
    <div className="rounded-2xl border border-border bg-card p-10 text-center text-sm text-muted-foreground">
      Loading admin section…
    </div>
  );
}

class AdminTabBoundary extends Component<
  { tab: AdminTab; children: ReactNode },
  { error: Error | null; tab: AdminTab }
> {
  state: { error: Error | null; tab: AdminTab } = { error: null, tab: this.props.tab };

  static getDerivedStateFromProps(
    props: { tab: AdminTab; children: ReactNode },
    state: { error: Error | null; tab: AdminTab },
  ) {
    if (props.tab !== state.tab) return { error: null, tab: props.tab };
    return null;
  }

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("[Admin] tab render failed", { tab: this.props.tab, error, info });
  }

  render() {
    const error = this.state.error;
    if (!error) return this.props.children;
    return (
      <div className="rounded-2xl border border-destructive/40 bg-destructive/5 p-6 text-sm">
        <p className="font-semibold text-destructive">This admin tab failed to load.</p>
        <p className="mt-2 text-muted-foreground">
          The admin shell is still running; switch tabs or reload after the broken tab is fixed.
        </p>
        <code className="mt-3 block break-all rounded bg-muted px-3 py-2 text-xs text-destructive">
          {error.message}
        </code>
      </div>
    );
  }
}

function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-2xl border border-border bg-card p-6">
      <h2 className="font-display text-lg font-semibold">{title}</h2>
      <div className="mt-4">{children}</div>
    </div>
  );
}

function OverviewTab() {
  return (
    <div className="grid gap-4 md:grid-cols-2">
      <Card title="Welcome">
        <p className="text-sm text-muted-foreground">Use the tabs above to configure the storefront. Products, blog posts, and landing pages are seeded — edit them in the backend dashboard. Reviews are pre-approved.</p>
      </Card>
      <Card title="Quick links">
        <div className="space-y-2 text-sm">
          <Link to="/shop" className="block text-primary hover:underline">View shop</Link>
          <Link to="/blog" className="block text-primary hover:underline">View blog</Link>
          <Link to="/" className="block text-primary hover:underline">View homepage</Link>
        </div>
      </Card>
    </div>
  );
}

function SettingsTab() {
  const { data, refetch } = useQuery({
    queryKey: ["settings"],
    queryFn: async () => (await supabase.from("site_settings").select("*").eq("id", 1).maybeSingle()).data,
  });
  const [form, setForm] = useState<any>(null);
  useEffect(() => { if (data && !form) setForm(data); }, [data, form]);
  if (!form) return null;

  async function save() {
    const { error } = await supabase.from("site_settings").update({
      brand_name: form.brand_name,
      tagline: form.tagline,
      logo_url: form.logo_url,
      favicon_url: form.favicon_url,
      promo_banner_text: form.promo_banner_text,
      promo_banner_enabled: form.promo_banner_enabled,
      free_ship_threshold_cents: form.free_ship_threshold_cents,
      email_signup_offer: form.email_signup_offer,
      contact_email: form.contact_email,
    }).eq("id", 1);
    if (error) toast.error(error.message); else { toast.success("Saved"); refetch(); }
  }

  return (
    <Card title="Site settings">
      <div className="grid gap-3 sm:grid-cols-2">
        <TextField label="Brand name" value={form.brand_name} onChange={(v) => setForm({ ...form, brand_name: v })} />
        <TextField label="Contact email" value={form.contact_email ?? ""} onChange={(v) => setForm({ ...form, contact_email: v })} />
        <TextField className="sm:col-span-2" label="Tagline" value={form.tagline ?? ""} onChange={(v) => setForm({ ...form, tagline: v })} />
        <div className="sm:col-span-2 grid gap-3 sm:grid-cols-2">
          <MediaPickerField label="Logo" value={form.logo_url ?? null} onChange={(v: string | null) => setForm({ ...form, logo_url: v })} />
          <MediaPickerField label="Favicon" value={form.favicon_url ?? null} onChange={(v: string | null) => setForm({ ...form, favicon_url: v })} />
        </div>
        <TextField className="sm:col-span-2" label="Promo banner text" value={form.promo_banner_text ?? ""} onChange={(v) => setForm({ ...form, promo_banner_text: v })} />
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.promo_banner_enabled} onChange={(e) => setForm({ ...form, promo_banner_enabled: e.target.checked })} /> Show promo banner</label>
        <TextField type="number" label="Free shipping threshold (cents)" value={String(form.free_ship_threshold_cents)} onChange={(v) => setForm({ ...form, free_ship_threshold_cents: Number(v) })} />
        <TextField className="sm:col-span-2" label="Email signup offer text" value={form.email_signup_offer ?? ""} onChange={(v) => setForm({ ...form, email_signup_offer: v })} />
      </div>
      <button onClick={save} className="mt-4 rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground shadow-glow hover:opacity-90">Save</button>
    </Card>
  );
}

function TrackingTab() {
  const { data, refetch } = useQuery({
    queryKey: ["tracking-cfg"],
    queryFn: async () => (await supabase.from("tracking_config").select("*").eq("id", 1).maybeSingle()).data,
  });
  const [form, setForm] = useState<any>(null);
  useEffect(() => { if (data && !form) setForm(data); }, [data, form]);
  if (!form) return null;
  async function save() {
    const { error } = await supabase.from("tracking_config").update({
      gtm_id: form.gtm_id, ga4_measurement_id: form.ga4_measurement_id,
      google_ads_conversion_id: form.google_ads_conversion_id, google_ads_conversion_label: form.google_ads_conversion_label,
      google_merchant_id: form.google_merchant_id,
      meta_pixel_id: form.meta_pixel_id, tiktok_pixel_id: form.tiktok_pixel_id,
      pinterest_tag_id: form.pinterest_tag_id, snapchat_pixel_id: form.snapchat_pixel_id, microsoft_uet_id: form.microsoft_uet_id,
    }).eq("id", 1);
    if (error) toast.error(error.message); else { toast.success("Saved"); refetch(); }
  }
  return (
    <Card title="Tracking & analytics IDs">
      <p className="mb-4 text-sm text-muted-foreground">Public IDs only. Server-side secrets (Meta CAPI token, TikTok Events API token) live in the project's backend secrets. Pixels only load after a visitor accepts cookies.</p>
      <div className="grid gap-3 sm:grid-cols-2">
        {[
          ["gtm_id", "Google Tag Manager ID (GTM-XXXX)"],
          ["ga4_measurement_id", "GA4 Measurement ID (G-XXXX)"],
          ["google_ads_conversion_id", "Google Ads conversion ID"],
          ["google_ads_conversion_label", "Google Ads conversion label"],
          ["google_merchant_id", "Google Merchant Center ID"],
          ["meta_pixel_id", "Meta Pixel ID"],
          ["tiktok_pixel_id", "TikTok Pixel ID"],
          ["pinterest_tag_id", "Pinterest Tag ID"],
          ["snapchat_pixel_id", "Snapchat Pixel ID"],
          ["microsoft_uet_id", "Microsoft UET Tag ID"],
        ].map(([k, label]) => (
          <TextField key={k} label={label} value={form[k] ?? ""} onChange={(v) => setForm({ ...form, [k]: v })} />
        ))}
      </div>
      <button onClick={save} className="mt-4 rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground shadow-glow hover:opacity-90">Save</button>
    </Card>
  );
}

function AmazonTab() {
  const { data, refetch } = useQuery({
    queryKey: ["amazon-cfg"],
    queryFn: async () => (await supabase.from("amazon_config").select("*").eq("id", 1).maybeSingle()).data,
  });
  const [form, setForm] = useState<any>(null);
  useEffect(() => { if (data && !form) setForm(data); }, [data, form]);
  if (!form) return null;
  async function save() {
    const { error } = await supabase.from("amazon_config").update({
      seller_central_status: form.seller_central_status,
      brand_registry_status: form.brand_registry_status,
      first_asin_status: form.first_asin_status,
      fba_mcf_status: form.fba_mcf_status,
      attribution_status: form.attribution_status,
      buy_with_prime_status: form.buy_with_prime_status,
      amazon_store_url: form.amazon_store_url,
      default_asin: form.default_asin,
      notes: form.notes,
    }).eq("id", 1);
    if (error) toast.error(error.message); else { toast.success("Saved"); refetch(); }
  }
  const STATUSES = ["not_started", "in_progress", "live"];
  return (
    <div className="space-y-4">
      <Card title="Amazon setup checklist">
        <p className="mb-4 text-sm text-muted-foreground">Track your progress getting on Amazon. Update each step's status as you go.</p>
        <div className="grid gap-3 sm:grid-cols-2">
          {[
            ["seller_central_status", "Seller Central account"],
            ["brand_registry_status", "Brand Registry"],
            ["first_asin_status", "First ASIN created"],
            ["fba_mcf_status", "FBA / MCF enabled"],
            ["attribution_status", "Amazon Attribution access"],
            ["buy_with_prime_status", "Buy with Prime"],
          ].map(([k, label]) => (
            <label key={k} className="text-sm">
              <span className="mb-1 block font-medium">{label}</span>
              <select value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="h-11 w-full rounded-lg border border-input bg-background px-3 text-sm">
                {STATUSES.map((s) => <option key={s} value={s}>{s.replaceAll("_", " ")}</option>)}
              </select>
            </label>
          ))}
        </div>
        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          <TextField label="Amazon Store URL" value={form.amazon_store_url ?? ""} onChange={(v) => setForm({ ...form, amazon_store_url: v })} />
          <TextField label="Default ASIN" value={form.default_asin ?? ""} onChange={(v) => setForm({ ...form, default_asin: v })} />
        </div>
        <label className="mt-3 block text-sm">
          <span className="mb-1 block font-medium">Notes</span>
          <textarea rows={3} value={form.notes ?? ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="w-full rounded-lg border border-input bg-background p-3 text-sm" />
        </label>
        <button onClick={save} className="mt-4 rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground shadow-glow hover:opacity-90">Save</button>
      </Card>
      <AmazonListingsCard />
    </div>
  );
}

function AmazonListingsCard() {
  const { data: listings, refetch } = useQuery({
    queryKey: ["amazon-listings"],
    queryFn: async () => (await supabase.from("amazon_listings").select("*").order("created_at", { ascending: false })).data ?? [],
  });
  const [editing, setEditing] = useState<any>(null);
  const isNew = editing && !editing.id;

  function startNew() {
    setEditing({ title: "", asin: "", channel: "amazon", description: "", notes: "", bullets: [] as string[] });
  }

  async function save() {
    const payload = {
      title: editing.title,
      asin: editing.asin || null,
      channel: editing.channel || "amazon",
      description: editing.description || null,
      notes: editing.notes || null,
      bullets: Array.isArray(editing.bullets) ? editing.bullets : String(editing.bullets ?? "").split("\n").filter(Boolean),
    };
    const q = isNew
      ? await supabase.from("amazon_listings").insert(payload)
      : await supabase.from("amazon_listings").update(payload).eq("id", editing.id);
    if (q.error) toast.error(q.error.message);
    else { toast.success("Saved"); setEditing(null); refetch(); }
  }

  async function remove(id: string) {
    if (!confirm("Delete this listing?")) return;
    const { error } = await supabase.from("amazon_listings").delete().eq("id", id);
    if (error) toast.error(error.message); else { toast.success("Deleted"); refetch(); }
  }

  return (
    <Card title="Amazon listings">
      <div className="mb-3 flex items-center justify-between">
        <p className="text-sm text-muted-foreground">Manage listing content (title, ASIN, bullets, description) for Amazon and other channels.</p>
        <button onClick={startNew} className="rounded-full bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground shadow-glow hover:opacity-90">New listing</button>
      </div>
      <table className="w-full text-left text-sm">
        <thead><tr className="text-muted-foreground"><th className="py-2">Title</th><th>ASIN</th><th>Channel</th><th></th></tr></thead>
        <tbody>
          {(listings ?? []).map((l: any) => (
            <tr key={l.id} className="border-t border-border">
              <td className="py-2">{l.title}</td>
              <td>{l.asin ?? "—"}</td>
              <td>{l.channel}</td>
              <td className="text-right">
                <button onClick={() => setEditing({ ...l, bullets: (l.bullets ?? []).join("\n") })} className="mr-2 text-primary hover:underline">Edit</button>
                <button onClick={() => remove(l.id)} className="text-destructive hover:underline">Delete</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {(listings ?? []).length === 0 && <p className="mt-3 text-sm text-muted-foreground">No listings yet. Click "New listing" to add one.</p>}

      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={() => setEditing(null)}>
          <div className="w-full max-w-2xl rounded-2xl border border-border bg-card p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-display text-lg font-semibold">{isNew ? "New listing" : "Edit listing"}</h3>
            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              <TextField className="sm:col-span-2" label="Title" value={editing.title ?? ""} onChange={(v) => setEditing({ ...editing, title: v })} />
              <TextField label="ASIN" value={editing.asin ?? ""} onChange={(v) => setEditing({ ...editing, asin: v })} />
              <label className="text-sm">
                <span className="mb-1 block font-medium">Channel</span>
                <select value={editing.channel ?? "amazon"} onChange={(e) => setEditing({ ...editing, channel: e.target.value })} className="h-11 w-full rounded-lg border border-input bg-background px-3 text-sm">
                  {["amazon", "meta", "tiktok", "google", "pinterest", "snap"].map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </label>
              <label className="text-sm sm:col-span-2">
                <span className="mb-1 block font-medium">Bullets (one per line)</span>
                <textarea rows={5} value={editing.bullets ?? ""} onChange={(e) => setEditing({ ...editing, bullets: e.target.value })} className="w-full rounded-lg border border-input bg-background p-3 text-sm" />
              </label>
              <label className="text-sm sm:col-span-2">
                <span className="mb-1 block font-medium">Description</span>
                <textarea rows={4} value={editing.description ?? ""} onChange={(e) => setEditing({ ...editing, description: e.target.value })} className="w-full rounded-lg border border-input bg-background p-3 text-sm" />
              </label>
              <label className="text-sm sm:col-span-2">
                <span className="mb-1 block font-medium">Notes</span>
                <textarea rows={2} value={editing.notes ?? ""} onChange={(e) => setEditing({ ...editing, notes: e.target.value })} className="w-full rounded-lg border border-input bg-background p-3 text-sm" />
              </label>
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <button onClick={() => setEditing(null)} className="rounded-full border border-border px-4 py-2 text-sm hover:bg-secondary">Cancel</button>
              <button onClick={save} className="rounded-full bg-primary px-5 py-2 text-sm font-semibold text-primary-foreground shadow-glow hover:opacity-90">Save</button>
            </div>
          </div>
        </div>
      )}
    </Card>
  );
}


function OrdersTab() {
  const { data: orders } = useQuery({
    queryKey: ["orders"],
    queryFn: async () => (await supabase.from("orders").select("*").order("created_at", { ascending: false }).limit(50)).data ?? [],
  });
  return (
    <Card title="Recent orders">
      <table className="w-full text-left text-sm">
        <thead><tr className="text-muted-foreground"><th className="py-2">Date</th><th>Email</th><th>Total</th><th>Status</th></tr></thead>
        <tbody>
          {(orders ?? []).map((o: any) => (
            <tr key={o.id} className="border-t border-border">
              <td className="py-2">{new Date(o.created_at).toLocaleString()}</td>
              <td>{o.customer_email ?? "—"}</td>
              <td>${(o.amount_total_cents / 100).toFixed(2)}</td>
              <td>{o.status}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {(orders ?? []).length === 0 && <p className="mt-3 text-sm text-muted-foreground">No orders yet.</p>}
    </Card>
  );
}

function LeadsTab() {
  const { data: leads } = useQuery({
    queryKey: ["leads"],
    queryFn: async () => (await supabase.from("leads").select("*").order("created_at", { ascending: false }).limit(100)).data ?? [],
  });
  return (
    <Card title="Recent leads">
      <table className="w-full text-left text-sm">
        <thead><tr className="text-muted-foreground"><th className="py-2">Date</th><th>Type</th><th>Email</th><th>Source</th></tr></thead>
        <tbody>
          {(leads ?? []).map((l: any) => (
            <tr key={l.id} className="border-t border-border">
              <td className="py-2">{new Date(l.created_at).toLocaleString()}</td>
              <td>{l.type}</td>
              <td>{l.email ?? l.phone}</td>
              <td>{l.source ?? "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {(leads ?? []).length === 0 && <p className="mt-3 text-sm text-muted-foreground">No leads yet.</p>}
    </Card>
  );
}

function TextField({ label, value, onChange, type = "text", className }: { label: string; value: string; onChange: (v: string) => void; type?: string; className?: string }) {
  return (
    <label className={`text-sm ${className ?? ""}`}>
      <span className="mb-1 block font-medium">{label}</span>
      <input type={type} value={value} onChange={(e) => onChange(e.target.value)} className="h-11 w-full rounded-lg border border-input bg-background px-3 text-sm" />
    </label>
  );
}
